import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { clearAuthToken } from '../auth/authStorage';

const STORAGE_KEYS = {
  authToken: 'authToken',
  searchHistory: 'myApp:searchHistory',
  theme: 'myApp:theme',
  language: 'myApp:language'
};
const MAX_HISTORY = 5;
const PRIVATE_CACHE_PREFIXES = ['stocksleuth:user:', 'stocksleuth:portfolio:', 'stocksleuth:watchlists:'];

export const clearPrivateCaches = () => {
  try {
    Object.keys(localStorage).forEach((key) => {
      if (PRIVATE_CACHE_PREFIXES.some((prefix) => key.startsWith(prefix))) localStorage.removeItem(key);
    });
    sessionStorage.clear();
  } catch {}
};

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  // user auth state
  const [user, setUser] = useState(null);
  const isLoggedIn = user !== null;

  // persistent preferences
  // Each history item: { symbol: string, name: string, logoUrl: string | null }
  const [searchHistory, setSearchHistory] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.searchHistory);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return parsed.map(item => {
        if (typeof item === 'string') {
          return { symbol: item, name: item, logoUrl: null };
        }
        return {
          symbol: item.symbol,
          name: item.name || item.symbol,
          logoUrl: item.logoUrl ?? null
        };
      });
    } catch {
      return [];
    }
  });

  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEYS.theme) || 'light'; } catch { return 'light'; }
  });
  const [language, setLanguage] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEYS.language) || 'en'; } catch { return 'en'; }
  });

  // persist preferences on change
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEYS.searchHistory,
        JSON.stringify(searchHistory)
      );
    } catch {}
  }, [searchHistory]);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEYS.theme, theme); } catch {}
  }, [theme]);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEYS.language, language); } catch {}
  }, [language]);

  /**
   * Add a search item. Accepts object with { symbol, name, logoUrl }.
   */
  const addSearch = ({ symbol, name, logoUrl }) => {
    setSearchHistory(prev => {
      const deduped = prev.filter(i => i.symbol !== symbol);
      return [{ symbol, name, logoUrl }, ...deduped].slice(0, MAX_HISTORY);
    });
  };

  const clearHistory = () => setSearchHistory([]);

  const logout = useCallback(() => {
    clearAuthToken();
    clearPrivateCaches();
    setUser(null);
  }, []);

  const value = {
    user,
    setUser,
    isLoggedIn,
    logout,
    // preferences
    searchHistory,
    addSearch,
    clearHistory,
    theme,
    setTheme,
    language,
    setLanguage
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error('useUser must be used within a UserProvider');
  return context;
};

export default UserContext;
